import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePlayerProfile } from '../../../features/playerProfile/playerProfileSlice';
import { CARD, CardTitle, EditButton, BulletList, SECONDARY_BUTTON } from './profileCard';

export const PreviousTeamsSection = () => {
  const dispatch = useDispatch();
  const { profile, updateLoading } = useSelector((state) => state.playerProfile);

  const [teams, setTeams] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newTeam, setNewTeam] = useState('');

  useEffect(() => {
    if (profile?.previousTeams) {
      const parsed = String(profile.previousTeams)
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      setTeams(parsed);
    } else {
      setTeams([]);
    }
  }, [profile]);

  const save = (updatedTeams) => {
    dispatch(updatePlayerProfile({ previousTeams: updatedTeams.join(', ') }));
  };

  const handleAdd = () => {
    const trimmed = newTeam.trim();
    if (!trimmed) return;
    const updated = [...teams, trimmed];
    setTeams(updated);
    save(updated);
    setNewTeam('');
    setIsAdding(false);
  };

  const handleDelete = (index) => {
    const updated = teams.filter((_, i) => i !== index);
    setTeams(updated);
    save(updated);
  };

  return (
    <section className={`${CARD} min-w-0`}>
      <CardTitle title="Previous Teams">
        {!isEditing ? (
          <EditButton onClick={() => setIsEditing(true)}>Edit teams</EditButton>
        ) : (
          <button
            type="button"
            onClick={() => { setIsEditing(false); setIsAdding(false); setNewTeam(''); }}
            className={SECONDARY_BUTTON}
          >
            Done
          </button>
        )}
      </CardTitle>

      {!isEditing ? (
        <BulletList items={teams} empty="No previous teams yet." />
      ) : (
      <div className="flex flex-wrap gap-2">
        {teams.map((team, i) => (
          <div
            key={i}
            className="px-4 py-2 rounded-full bg-jp-accent/10 border border-jp-accent/40 text-jp-accent text-sm font-medium flex items-center gap-2 group"
          >
            <span>{team}</span>
            {isEditing && (
              <button
                onClick={() => handleDelete(i)}
                disabled={updateLoading}
                className="text-red-600 dark:text-red-400 hover:text-red-500 disabled:opacity-50"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            )}
          </div>
        ))}

        {/* Add Team Input or Button - Only show when editing */}
        {isEditing && isAdding ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newTeam}
              onChange={(e) => setNewTeam(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="Enter team name"
              className="px-4 py-2 rounded-full bg-jp-section border border-jp-line text-jp-ink text-sm focus:outline-none focus:border-jp-accent"
              autoFocus
              disabled={updateLoading}
            />
            <button
              onClick={handleAdd}
              disabled={updateLoading || !newTeam.trim()}
              className="px-3 py-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updateLoading && (
                <div className="w-3 h-3 mr-1.5 border-2 border-jp-line border-t-transparent rounded-full animate-spin"></div>
              )}
              Add
            </button>
            <button
              onClick={() => { setIsAdding(false); setNewTeam(''); }}
              disabled={updateLoading}
              className="px-3 py-2 rounded-full bg-jp-section hover:bg-jp-line text-jp-ink text-sm font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : isEditing ? (
          <button
            onClick={() => setIsAdding(true)}
            disabled={updateLoading}
            className="px-4 py-2 rounded-full border-2 border-dashed border-jp-line text-jp-muted hover:border-jp-accent hover:text-jp-accent text-sm font-medium transition-colors disabled:opacity-50"
          >
            + Add Team
          </button>
        ) : null}
      </div>
      )}
    </section>
  );
};

export default PreviousTeamsSection;
